/**
 * Checklists and routines — ADR-001 §7.3, §9 (`checklists.generate`), §11.9; guide §5.5 "Checklists"; the
 * prototype's TEMPLATES / CHK / ROUTINE / WK_TP / wkInstance / ckStatus / bulkRoutine (docs/prototype.html
 * L578–L590, L1019–L1045, L1768–L1840, L2682, L4326).
 *
 * Two recurring things, kept apart as in the ADR:
 *  - a monthly CHECKLIST per client (copied from a segment template, or typed in): each item has a due day of
 *    the month; the 06:00 job makes this month's run (and a task for the SPOC) on that day — `checklist_runs`
 *    unique `(item, period)`, so a re-run never duplicates;
 *  - ROUTINES: daily / weekly / fortnightly / monthly recurring tasks; the job materialises the current
 *    instance — `routine_instances` unique `(routine, due_on)`; a done instance is never regenerated.
 *
 * Visibility follows the client: managers all clients; an ops_member the clients they are the current SPOC of
 * (`checklist.write.own` — no approval step, operator decision); requesters and HR none (ids → 404).
 * A tick needs proof: a note, a file, or both (`422` with neither). Every write is audited.
 */
import { z } from 'zod';
import { IsoDate, Timestamp, Uuid, list } from './common.js';
import { ClientRef, Priority, Segment } from './clients.js';
import { TaskPriority } from './tasks.js';
import { UserRef } from './users.js';
// ============================================================================ shared pieces
/** Day of the month an item falls due. 29–31 fall on the month's last day in a shorter month. */
export const DueDayOfMonth = z.number().int().min(1, 'Pick a day from 1 to 31').max(31, 'Pick a day from 1 to 31');
const ItemText = z.string().trim().min(3, 'Say what has to be done (at least 3 characters)').max(200, 'Keep it under 200 characters');
const Name = z.string().trim().min(2, 'Give it a name of at least 2 characters').max(80);
/** Proof on a tick (prototype ckConfirm): a note, a file, or both — one is enough, none is a `422`. */
const ProofNote = z.string().trim().min(3, 'Write a few words about what was done').max(1000);
/** A key from a completed upload made by the same person (the API checks it; never a free-form path). */
const ProofFileKey = z.string().min(1).max(500);
export const TickProofBody = z
    .object({ note: ProofNote.optional(), file_key: ProofFileKey.optional() })
    .strict()
    .refine((b) => !!b.note || !!b.file_key, { message: 'Add a note or a file as proof', path: ['note'] });
export const ChecklistProof = z
    .object({ note: z.string().nullable(), file_key: z.string().nullable(), by: UserRef, at: Timestamp })
    .meta({ id: 'ChecklistProof' });
/**
 * One item's state this period (prototype ckStatus): `done`; `missed` (past its due day, not done — flag #10);
 * `due_today`; `upcoming`; `not_started` (a fortnightly routine starting next week: no instance yet).
 */
export const ChecklistItemState = z.enum(['done', 'missed', 'due_today', 'upcoming', 'not_started']).meta({ id: 'ChecklistItemState' });
/** Chip wording: "Done", "Missed by 3d", "Due today", "Due 15 Oct" (monthly) / "Due Wed" (weekly), "Starts next week". */
const StateLabel = z.string();
// ============================================================================ templates (manager)
export const ChecklistTemplateItem = z
    .object({
    id: Uuid,
    text: z.string(),
    due_day_of_month: DueDayOfMonth,
    default_priority: TaskPriority,
    sort_order: z.number().int(),
    active: z.boolean(),
})
    .meta({ id: 'ChecklistTemplateItem' });
export const ChecklistTemplate = z
    .object({
    id: Uuid,
    name: z.string(),
    /** The segment it is the standard list for; `null` = an ad-hoc template. */
    segment: Segment.nullable(),
    active: z.boolean(),
    items: z.array(ChecklistTemplateItem),
    /** Client checklists made from it that are still active. */
    clients_using: z.number().int().nonnegative(),
    version: z.number().int().positive(),
    updated_at: Timestamp,
})
    .meta({ id: 'ChecklistTemplate' });
export const ChecklistTemplateList = list(ChecklistTemplate).meta({ id: 'ChecklistTemplateList' });
export const NewTemplateItem = z
    .object({ text: ItemText, due_day_of_month: DueDayOfMonth, default_priority: TaskPriority.default('medium') })
    .strict();
/**
 * POST /checklist-templates. The prototype's "paste one activity per line, then a comma and the due day" is
 * parsed by the SPA into `items`. One active template per segment: a second for the same segment is a `409`.
 */
export const CreateTemplateBody = z
    .object({ name: Name, segment: Segment.nullable().default(null), items: z.array(NewTemplateItem).min(1, 'Add at least one line').max(60) })
    .strict();
/** PATCH /checklist-templates/{id} (If-Match). Changing a template never rewrites checklists already made from it. */
export const UpdateTemplateBody = z
    .object({ name: Name.optional(), segment: Segment.nullable().optional(), active: z.boolean().optional() })
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one thing' });
/** POST /checklist-templates/{id}/items — append lines. */
export const AddTemplateItemsBody = z.object({ items: z.array(NewTemplateItem).min(1).max(60) }).strict();
/** PATCH /checklist-templates/{id}/items/{iid} — fix a line or switch it off. */
export const UpdateTemplateItemBody = z
    .object({ text: ItemText.optional(), due_day_of_month: DueDayOfMonth.optional(), default_priority: TaskPriority.optional(), active: z.boolean().optional() })
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one thing' });
export const TemplateItemParams = z.object({ id: Uuid, iid: Uuid });
// ============================================================================ client checklists
/**
 * This period's run of one item (`checklist_runs`). The 06:00 job makes a run for every active item whose due date
 * has come and that has no run yet this period (or last period: a month ending on non-working days), so a due day
 * on a weekend or holiday is made on the next working day — late, already missed. Absent run = its day has not come,
 * or the job has not run since. Missed and flag #10 follow `due_on`, not whether a run exists.
 */
export const ChecklistRun = z
    .object({
    id: Uuid,
    item_id: Uuid,
    client_id: Uuid,
    /** `YYYY-MM`. */
    period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
    /** The task made for the SPOC on the due day; closing that task ticks the run with the task's proof. */
    task_id: Uuid.nullable(),
    done: z.boolean(),
    proof: ChecklistProof.nullable(),
    generated_at: Timestamp,
})
    .meta({ id: 'ChecklistRun' });
export const ChecklistItem = z
    .object({
    id: Uuid,
    text: z.string(),
    due_day_of_month: DueDayOfMonth,
    /** The date it falls due this month (29–31 clamp to the month's last day). */
    due_on: IsoDate,
    priority: TaskPriority,
    active: z.boolean(),
    /** Never null. `run_id` / `task_id` are `null` until the run is made; `state` follows `due_on` either way (`missed` past it). */
    this_period: z
        .object({ run_id: Uuid.nullable(), task_id: Uuid.nullable(), done: z.boolean(), proof: ChecklistProof.nullable(), state: ChecklistItemState, label: StateLabel })
        .meta({ id: 'ChecklistItemPeriod' }),
})
    .meta({ id: 'ChecklistItem' });
export const ClientChecklist = z
    .object({
    id: Uuid,
    client: ClientRef,
    name: z.string(),
    /** The template it was copied from; `null` = typed in. Later template edits do not change it. */
    template: z.object({ id: Uuid, name: z.string() }).nullable(),
    active: z.boolean(),
    /** This month, e.g. `2026-10`. */
    period: z.string(),
    items: z.array(ChecklistItem),
    /** Active items done this period ÷ active items, rounded (prototype ckProg). 0 with no items. */
    progress_pct: z.number().int().min(0).max(100),
    /** Active items past their due day and not done (prototype ckMissed). */
    missed_count: z.number().int().nonnegative(),
    created_by: UserRef,
    /** If-Match for PATCHes of the checklist and its items (an item change bumps it). */
    version: z.number().int().positive(),
    updated_at: Timestamp,
})
    .meta({ id: 'ClientChecklist' });
export const ClientChecklistList = list(ClientChecklist).meta({ id: 'ClientChecklistList' });
export const NewChecklistItem = z.object({ text: ItemText, due_day_of_month: DueDayOfMonth, priority: TaskPriority.default('medium') }).strict();
/**
 * POST /clients/{id}/checklists — no approval step. With `template_id` and no `items`, the template's active
 * items are copied; with both, `items` are used and the template is only recorded as the source.
 */
export const CreateClientChecklistBody = z
    .object({ template_id: Uuid.optional(), name: Name, items: z.array(NewChecklistItem).max(60).optional() })
    .strict()
    .refine((b) => !!b.template_id || (b.items?.length ?? 0) > 0, { message: 'Pick a template or add at least one item', path: ['items'] });
/** PATCH /client-checklists/{id} (If-Match = checklist version). Switching it off stops new runs; this month's open runs stay. */
export const UpdateClientChecklistBody = z
    .object({ name: Name.optional(), active: z.boolean().optional() })
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one thing' });
/** POST /client-checklists/{id}/items. */
export const AddChecklistItemsBody = z.object({ items: z.array(NewChecklistItem).min(1).max(60) }).strict();
/** PATCH /client-checklists/{id}/items/{iid} (If-Match = checklist version). A changed due day applies from the next run; a run already made keeps its task. */
export const UpdateChecklistItemBody = z
    .object({ text: ItemText.optional(), due_day_of_month: DueDayOfMonth.optional(), priority: TaskPriority.optional(), active: z.boolean().optional() })
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one thing' });
export const ChecklistItemParams = z.object({ id: Uuid, iid: Uuid });
/** POST /checklists/apply-template — copy a template to many clients as their checklist. */
export const ApplyTemplateBody = z
    .object({ template_id: Uuid, client_ids: z.array(Uuid).min(1, 'Choose at least one client').max(500) })
    .strict()
    .refine((b) => new Set(b.client_ids).size === b.client_ids.length, { message: 'List each client once', path: ['client_ids'] });
/** `already_has_it` = the client already has an active checklist from this template. */
export const ApplySkipReason = z.enum(['already_has_it', 'client_archived']).meta({ id: 'ApplySkipReason' });
export const ApplyTemplateResponse = z
    .object({
    applied: z.array(z.object({ client_id: Uuid, checklist_id: Uuid })),
    skipped: z.array(z.object({ client_id: Uuid, reason: ApplySkipReason })),
})
    .meta({ id: 'ApplyTemplateResponse' });
// ============================================================================ routines
export const ROUTINE_EVERY = ['day', 'week', 'fortnight', 'month'];
export const RoutineEvery = z.enum(ROUTINE_EVERY).meta({ id: 'RoutineEvery' });
/** Prototype EVERY wording. */
export const ROUTINE_EVERY_LABEL = { day: 'Daily', week: 'Weekly', fortnight: 'Every 2 weeks', month: 'Monthly' };
export const Weekday = z.enum(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']).meta({ id: 'Weekday' });
export const StartPhase = z.enum(['this', 'next']).meta({ id: 'StartPhase' });
/** Monthly routines run on day 1–28 (prototype: every month has it). */
export const RoutineDayOfMonth = z.number().int('Pick a day from 1 to 28').min(1, 'Pick a day from 1 to 28').max(28, 'Pick a day from 1 to 28');
const RoutineFields = {
    what: ItemText,
    every: RoutineEvery,
    /** Required for week and fortnight; not allowed otherwise. */
    weekday: Weekday.optional(),
    /** Required for month (1–28); not allowed otherwise. */
    day_of_month: RoutineDayOfMonth.optional(),
    /** Fortnight only: start this week or next (default `this`). */
    start_phase: StartPhase.optional(),
    /** Optional link to a usage product the routine is about (e.g. "Update the usage log"). */
    usage_product_key: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/).optional(),
};
/** The when-rules as one check, so create, bulk and the merged PATCH apply the same one. */
export function routineWhenIssues(r) {
    const out = [];
    const weekly = r.every === 'week' || r.every === 'fortnight';
    if (weekly && r.weekday == null)
        out.push({ path: 'weekday', message: 'Pick the day of the week' });
    if (!weekly && r.weekday != null)
        out.push({ path: 'weekday', message: 'A day of the week is only for weekly and every-2-weeks tasks' });
    if (r.every === 'month' && r.day_of_month == null)
        out.push({ path: 'day_of_month', message: 'Pick the day of the month' });
    if (r.every !== 'month' && r.day_of_month != null)
        out.push({ path: 'day_of_month', message: 'A day of the month is only for monthly tasks' });
    if (r.every !== 'fortnight' && r.start_phase != null)
        out.push({ path: 'start_phase', message: 'Starting this or next week is only for every-2-weeks tasks' });
    return out;
}
const whenRefine = (r, ctx) => routineWhenIssues(r).forEach((i) => ctx.addIssue({ code: 'custom', path: [i.path], message: i.message }));
/** The current copy of a routine (`routine_instances`): due this period, with its proof once ticked. */
export const RoutineInstance = z
    .object({
    id: Uuid,
    due_on: IsoDate,
    /** The task in the SPOC's plan for it; closing that task ticks the instance with the task's proof. */
    task_id: Uuid.nullable(),
    done: z.boolean(),
    proof: ChecklistProof.nullable(),
    state: ChecklistItemState,
    label: StateLabel,
})
    .meta({ id: 'RoutineInstance' });
export const ClientRoutine = z
    .object({
    id: Uuid,
    client_id: Uuid,
    what: z.string(),
    every: RoutineEvery,
    weekday: Weekday.nullable(),
    day_of_month: RoutineDayOfMonth.nullable(),
    start_phase: StartPhase.nullable(),
    usage_product_key: z.string().nullable(),
    /** Prototype whenText: "Every working day", "Every Wed", "Every other Wed, starting next week", "On day 28 of the month". */
    when_text: z.string(),
    active: z.boolean(),
    /** The copy due now; `null` when none is due yet (state `not_started`: "Starts next week"). */
    current: RoutineInstance.nullable(),
    created_by: UserRef,
    /** If-Match for PATCH. */
    version: z.number().int().positive(),
    updated_at: Timestamp,
})
    .meta({ id: 'ClientRoutine' });
/** GET /clients/{id}/routines — Client 360 → Routine: the recurring tasks and the standard monthly list beside them. */
export const ClientRoutines = z
    .object({
    client: ClientRef,
    /** Active routines, grouped by the SPA in ROUTINE_EVERY order. */
    routines: z.array(ClientRoutine),
    /** The client's active monthly checklist items ("Standard monthly list"). */
    monthly_items: z.array(z.object({ id: Uuid, text: z.string(), due_day_of_month: DueDayOfMonth })),
    /** The caller may add, change and remove (manager, or this client's SPOC). */
    can_edit: z.boolean(),
})
    .meta({ id: 'ClientRoutines' });
export const CreateRoutineBody = z.object(RoutineFields).strict().superRefine(whenRefine);
/**
 * PATCH /clients/{id}/routines/{rid} (If-Match) — any field; merged with the stored routine and checked with
 * `routineWhenIssues` (`422`). `null` clears weekday / day_of_month / start_phase / usage_product_key. A change
 * rebuilds the current copy unless it is already done (prototype rebuildInst).
 */
export const UpdateRoutineBody = z
    .object({
    what: ItemText.optional(),
    every: RoutineEvery.optional(),
    weekday: Weekday.nullable().optional(),
    day_of_month: RoutineDayOfMonth.nullable().optional(),
    start_phase: StartPhase.nullable().optional(),
    usage_product_key: RoutineFields.usage_product_key.unwrap().nullable().optional(),
})
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one thing' });
export const RoutineParams = z.object({ id: Uuid, rid: Uuid });
// ---------------------------------------------------------------- bulk ("For many clients")
/** A sheet row as pasted/read by the SPA: columns Task, Frequency, Day, Clients (prototype bkFile template). */
export const RoutineSheetRow = z
    .object({
    task: z.string().max(200),
    /** "Daily", "Weekly", "Every 2 weeks", "Monthly" (case and spacing ignored; "fortnightly" accepted). */
    frequency: z.string().max(40),
    /** "Mon"–"Fri" (or the full name), or a day 1–28 for monthly; empty for daily. */
    day: z.string().max(20),
    /** "All", a segment ("BFSI"), "High priority", or client names separated by ";". */
    clients: z.string().max(4000),
})
    .strict();
/**
 * POST /routines/bulk. `pick`: one routine for the chosen clients. `sheet`: rows from an uploaded sheet, each
 * resolved on the server (clients by exact name, segment or "All"/"High priority"; a member's non-own clients are
 * skipped). `dry_run` previews without writing. Duplicates — same normalised `(what, every)` on the client — are
 * skipped, never added twice.
 */
export const BulkRoutinesBody = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('pick'), ...RoutineFields, client_ids: z.array(Uuid).min(1, 'Choose at least one client').max(500) }).strict().superRefine(whenRefine),
    z.object({ mode: z.literal('sheet'), rows: z.array(RoutineSheetRow).min(1, 'No rows found in that file').max(500), dry_run: z.boolean().default(false) }).strict(),
]);
export const BulkSkipReason = z
    .enum(['already_has_it', 'not_your_client', 'client_not_found', 'no_task', 'frequency_not_understood', 'day_not_understood', 'no_clients'])
    .meta({ id: 'BulkSkipReason' });
export const BulkRoutinesResponse = z
    .object({
    dry_run: z.boolean(),
    /** Routines added (or, on a dry run, that would be). */
    created: z.array(z.object({ client_id: Uuid, client_name: z.string(), routine_id: Uuid.nullable(), what: z.string(), every: RoutineEvery })),
    /** `row` = the 1-based sheet row; `null` in `pick` mode. `client_id` is `null` when no client was resolved. */
    skipped: z.array(z.object({ row: z.number().int().positive().nullable(), client_id: Uuid.nullable(), reason: BulkSkipReason, detail: z.string() })),
})
    .meta({ id: 'BulkRoutinesResponse' });
// ============================================================================ the Checklists screen (across clients)
/** One open item on the "This week" tab: due in the next 7 days, or already missed (checklist runs and routine copies). */
export const ChecklistDueItem = z
    .object({
    kind: z.enum(['checklist_run', 'routine_instance']),
    /** The run or instance id — what POST …/done takes. `null` for a checklist item whose run is not made yet (it can still be `missed`; tickable once made). */
    id: Uuid.nullable(),
    text: z.string(),
    client: ClientRef,
    due_on: IsoDate,
    /** "Monthly", "Weekly", "Daily", "Every 2 weeks" — the cadence chip (prototype cadLabel). */
    cadence: z.string(),
    state: ChecklistItemState,
    label: StateLabel,
})
    .meta({ id: 'ChecklistDueItem' });
export const ChecklistsOverviewQuery = z.object({
    /** Managers only: one SPOC's clients. Ignored for a member, who sees their own. */
    spoc_id: Uuid.optional(),
    segment: Segment.optional(),
    priority: Priority.optional(),
});
/** GET /checklists — the Checklists screen: four stats, the "This week" list, the "By client" table. */
export const ChecklistsOverview = z
    .object({
    as_of: IsoDate,
    period: z.string(),
    /** Over this month's checklist items and the current routine copies, for the clients in scope. */
    stats: z.object({
        items: z.number().int().nonnegative(),
        done: z.number().int().nonnegative(),
        missed: z.number().int().nonnegative(),
        due_today: z.number().int().nonnegative(),
    }),
    /** Not done, due within 7 days or missed, earliest first. */
    this_week: z.array(ChecklistDueItem),
    by_client: z.array(z.object({
        client: ClientRef,
        /** The checklists' template names, joined ("BFSI monthly ops"); "Custom" when typed in; `null` = no checklist. */
        template: z.string().nullable(),
        progress_pct: z.number().int().min(0).max(100),
        missed_count: z.number().int().nonnegative(),
    })),
    /** Set up for any client (managers) or own clients (members): the "Set up a checklist" bar. */
    can_set_up: z.boolean(),
})
    .meta({ id: 'ChecklistsOverview' });
//# sourceMappingURL=checklists.js.map